/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { alpha, styled } from "@mui/material/styles";
import { Box, ButtonBase, Chip, Paper, TextField, Typography } from "@mui/material";

const getSearchWidth = (isMobile) => (isMobile ? 150 : 450);

export const StyledGlobalSearchContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ isMobile }) => ({
  position: "relative",
  width: `${getSearchWidth(isMobile)}px`,
  marginLeft: "10px",
  zIndex: 20,
}));

export const StyledGlobalSearchField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})({
  width: "100%",
  '&:hover': {
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid #2b4fb9 !important',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
    borderRadius: '10px',
    borderColor: '#d8deeb',
  },
  '& .MuiInputBase-root': {
    borderRadius: '10px',
    height: '2.5rem',
    fontSize: '15px',
    width: '100%',
    backgroundColor: '#ffffff',
    paddingRight: '6px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#6b7280',
    opacity: 1,
  },
});

export const StyledSearchDropdown = styled(Paper)({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  width: '100%',
  borderRadius: '14px',
  border: '1px solid #d8deeb',
  boxShadow: '0 16px 32px rgba(15, 23, 42, 0.16)',
  overflow: 'hidden',
});

export const StyledSearchDropdownContent = styled(Box)({
  maxHeight: '420px',
  overflowY: 'auto',
  backgroundColor: '#ffffff',
});

export const StyledSearchStatus = styled(Typography)({
  padding: '16px',
  fontSize: '14px',
  color: '#64748b',
  textAlign: 'center',
});

export const StyledSearchResultItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})(({ highlighted }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '12px 14px',
  backgroundColor: highlighted ? '#f1f5f9' : '#ffffff',
  transition: 'background-color 0.18s ease',
  '&:hover': {
    backgroundColor: '#f8fafc',
  },
}));

export const StyledSearchResultIdentity = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minWidth: 0,
  flex: 1,
});

export const StyledSearchProfileBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'badgeColor',
})(({ badgeColor }) => ({
  width: '34px',
  height: '34px',
  minWidth: '34px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(badgeColor, 0.14),
  color: badgeColor,
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.02em',
}));

export const StyledSearchResultDetails = styled(Box)({
  minWidth: 0,
  flex: 1,
});

export const StyledSearchResultHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '6px',
  minWidth: 0,
  marginBottom: '4px',
});

export const StyledSearchResultName = styled(Typography)({
  fontSize: '14px',
  lineHeight: 1.2,
  fontWeight: 600,
  color: '#0f172a',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const StyledSearchTypeChip = styled(Chip)({
  height: '20px',
  borderRadius: '999px',
  backgroundColor: '#eef2f7',
  color: '#475569',
  '& .MuiChip-label': {
    paddingLeft: '8px',
    paddingRight: '8px',
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: 1,
    textTransform: 'lowercase',
  },
});

export const StyledSearchResultMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
  color: '#64748b',
});

export const StyledSearchResultPhone = styled(Typography)({
  fontSize: '12px',
  lineHeight: 1.3,
  color: '#64748b',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const StyledSearchRestaurant = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  flexShrink: 0,
  color: '#475569',
  maxWidth: '40%',
});

export const StyledSearchRestaurantLabel = styled(Typography)({
  fontSize: '12px',
  lineHeight: 1.3,
  fontWeight: 500,
  color: '#475569',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const StyledSearchDivider = styled(Box)({
  height: '1px',
  backgroundColor: '#eef2f7',
  margin: '0 14px',
});

export const StyledViewMoreButton = styled(ButtonBase)({
  width: '100%',
  justifyContent: 'center',
  padding: '12px 14px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#2b4fb9',
  backgroundColor: '#ffffff',
  transition: 'background-color 0.18s ease',
  '&:hover': {
    backgroundColor: '#f8fafc',
  },
});
